import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { PeopleFormData } from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchMetadata,
  selectAdvanceSearchPeopleForm,
} from '../../../../Selector/AdvanceSearchSelector';
import { FormField } from './FormField';

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface PeopleFormProps {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: PeopleFormData) => void;
  isLoading?: boolean;
}

export const PeopleForm: React.FC<PeopleFormProps> = ({ onValidityChange, onFormDataChange, isLoading }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectAdvanceSearchMetadata);
  const formData = useSelector(selectAdvanceSearchPeopleForm);
  const [countryId, setCountryId] = useState(0);
  const [regionId, setRegionId] = useState(0);
  const countryOptions: { id: number; label: string }[] = (filters?.countries ?? [])
    .map((c: { id: number; country_name: string }) => ({ id: c.id, label: c.country_name }));
  const regionOptions: { id: number; label: string }[] = (filters?.regions ?? [])
    .map((label: string, i: number) => ({ id: i + 1, label }));

  const updateField = (field: keyof PeopleFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchPeopleForm(newData));
  };

  const handleCountryChange = (id: number) => {
    setCountryId(id);
    const selectedLabel = countryOptions.find((option) => option.id === id)?.label ?? '';
    updateField('country', selectedLabel);
  };

  const handleRegionChange = (id: number) => {
    setRegionId(id);
    const selectedLabel = regionOptions.find((option) => option.id === id)?.label ?? '';
    updateField('region', selectedLabel);
  };

  useEffect(() => {
    const isValid = Object.values(formData).some((val) => {
      if (typeof val === 'number') {
        return val !== 0;
      }
      return String(val).trim() !== '';
    });
    onValidityChange?.(isValid);
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange, onValidityChange]);

  useEffect(() => {
    const matchedCountryId = countryOptions.find((option) => option.label === formData.country)?.id ?? 0;
    setCountryId(matchedCountryId);
  }, [countryOptions, formData.country]);

  useEffect(() => {
    const matchedRegionId = regionOptions.find((option) => option.label === formData.region)?.id ?? 0;
    setRegionId(matchedRegionId);
  }, [regionOptions, formData.region]);

  return (
    <FormContainer>
      <FormField
        label="Full Name"
        value={formData.fullName}
        onChange={(val) => updateField('fullName', val)}
        placeholder="Enter full name"
      />
      <FormField
        label="Co."
        value={formData.currentCo}
        onChange={(val) => updateField('currentCo', val)}
        placeholder="Current company name"
        sup="Current"
        isSupText={true}
      />
      <FormField
        label="Co."
        value={formData.previousCo}
        onChange={(val) => updateField('previousCo', val)}
        placeholder="Previous company name"
        sup="Previous"
        isSupText={true}
      />
      <FormField
        label="Job Title"
        value={formData.designation}
        onChange={(val) => updateField('designation', val)}
        placeholder="Enter your position"
      />
      <FormField
        label="Country"
        type="dropdown"
        value={countryId}
        onChange={(val) => handleCountryChange(val as number)}
        options={countryOptions}
        placeholder="Select country"
        isLoading={isLoading}
        hasSearch={true}
      />
      <FormField
        label="Region"
        type="dropdown"
        value={regionId}
        onChange={(val) => handleRegionChange(val as number)}
        options={regionOptions}
        placeholder="Select region"
        isLoading={isLoading}
        hasSearch={true}
      />
    </FormContainer>
  );
};